import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { FileText, Printer, RefreshCw } from 'lucide-react';
import PrintSignatureBlock from '../../components/PrintSignatureBlock';

export default function AdminReports() {
  const [kinds, setKinds] = useState([]);
  const [picked, setPicked] = useState('revenue');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/reports/kinds').then(r => setKinds(r.data)); }, []);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reports/generate', { params: { kind: picked, days } });
      setData(data);
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <style>{`@media print { .no-print { display: none !important; } }`}</style>
      <div className="mb-6 no-print">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">SUMMARY REPORTS</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Summary report generator</h1>
        <p className="text-fg-muted mt-2">Pick any clinic dimension — revenue, expenses, appointments, memberships, inventory, reviews, outcomes, students.</p>
      </div>

      <div className="admin-card mb-6 no-print">
        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Report</div>
            <select className="input" value={picked} onChange={e=>setPicked(e.target.value)} data-testid="report-kind">
              {kinds.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Window</div>
            <select className="input" value={days} onChange={e=>setDays(parseInt(e.target.value))} data-testid="report-days">
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </label>
          <div className="flex items-end gap-2">
            <button onClick={run} disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="report-run">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/> Generate
            </button>
            {data && (
              <button onClick={()=>window.print()} className="btn-secondary inline-flex items-center gap-2" data-testid="report-print">
                <Printer className="w-4 h-4"/> Print
              </button>
            )}
          </div>
        </div>
      </div>

      {data && (
        <div className="bento-card" data-testid="report-output">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-brand"/> {kinds.find(k=>k.id===picked)?.label || picked}
          </h3>
          {data.since_days && <p className="text-xs text-fg-muted mb-3">Since last {data.since_days} days</p>}
          <ReportTable rows={data.rows || []}/>
          {!data.rows?.length && <p className="text-fg-muted text-sm">No data for this window.</p>}
          <PrintSignatureBlock leftLabel="Authorised by" rightLabel="Date · printed signature"/>
        </div>
      )}
    </div>
  );
}

function ReportTable({ rows }) {
  if (!rows.length) return null;
  const cols = Object.keys(rows[0]);
  return (
    <table>
      <thead><tr>{cols.map(c => <th key={c} className="capitalize">{c.replace(/_/g, ' ')}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {cols.map(c => <td key={c} className="text-sm">{typeof r[c] === 'number' ? r[c].toLocaleString() : (r[c] ?? '—')}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
